import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Query, QueryResult } from "@material-table/core";
import { NotificationManager } from "react-notifications";
import DataTable from "../../../components/shared/DataTable/DataTable";
import InfoBox from "../../../components/shared/InfoBox/InfoBox";
import SearchBarWithActions from "../../../components/shared/SearchBarWithActions/SearchBarWithActions";
import Selector, { SelectorType } from "../../../components/shared/Selector/Selector";
import { Loading } from "../../../components/shared";
import PlaceSelector from "../../page-supplier-presentation/PlaceSelector";
import serviceSales, { TedNotice, TedCategory, TedCpvCode } from "../../../services/service-sales";
import { useDebouncedState } from "../../../util/hooks";
import { getDaysLeft } from "../../../util";
import { MultiValue } from "react-select";
import TedNoticeDetailPanel from "./TedNoticeDetailPanel";
import {
  placeSummary, categoryCell, SORT_KEY_BY_FIELD, cpvCodesForCategories, categoriesStillCovered,
} from "./tedDisplay";
import styles from "./TedTable.module.scss";
import AppTenderWatchButton from "../../app-tender-watch/app-tender-watch-button";
import { SharedTenderFilter } from "../../app-tender-watch/useSharedTenderFilter";

const PAGE_SIZE = 10;

// DataTable ignores defaultOrderByField unless given the column index too.
const DEFAULT_SORT_FIELD = "publicationDate";


const formatDate = (date?: string | null) =>
  (date ? moment(date).locale("sv").format("D MMMM YYYY") : "");

const formatValue = (amount?: number | null, currency?: string | null) => {
  if (amount === null || amount === undefined) return "–";
  try {
    return new Intl.NumberFormat("sv-SE", {
      style: currency ? "currency" : "decimal",
      currency: currency || undefined,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    // An unexpected currency code would otherwise throw and blank the whole table.
    return `${Math.round(amount).toLocaleString("sv-SE")} ${currency ?? ""}`.trim();
  }
};

type Props = {
  filter: SharedTenderFilter,
  /** Both tabs stay mounted once visited, so the one out of view must not keep querying. */
  hidden?: boolean,
};

/** Filters, sorts and pages on the server: the dataset grows without bound. */
export const TedTable: FC<Props> = ({ filter, hidden = false }) => {
  const [t] = useTranslation("requests");
  // material-table exposes no ref type; only onQueryChange is used.
  const tableRef = useRef<{ onQueryChange: (query?: { page?: number }) => void } | null>(null);

  const [search, setSearch, debouncedSearch] = useDebouncedState("", 300);
  const { areaIds, setAreaIds, places, setPlaces } = filter;
  const [categories, setCategories] = useState<TedCategory[]>([]);
  const [cpvOptions, setCpvOptions] = useState<TedCpvCode[]>([]);
  const [buyerOptions, setBuyerOptions] = useState<SelectorType[]>([]);
  const [selectedBuyers, setSelectedBuyers] = useState<MultiValue<SelectorType>>([]);
  const [selectedCpv, setSelectedCpv] = useState<MultiValue<SelectorType>>([]);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Settled rather than all: one selector failing to load should not blank the other.
  useEffect(() => {
    (async () => {
      const [categoryResult, cpvResult, buyerResult] = await Promise.allSettled([
        serviceSales.fetchTedCategories(),
        serviceSales.fetchTedCpvCodes(),
        serviceSales.fetchTedBuyers(),
      ]);

      if (categoryResult.status === "fulfilled") setCategories(categoryResult.value);
      else {
        NotificationManager.error(
          String(categoryResult.reason),
          t("published.ted.categoriesError", "Kunde inte hämta kategorier"),
        );
      }

      if (cpvResult.status === "fulfilled") setCpvOptions(cpvResult.value);
      else {
        NotificationManager.error(
          String(cpvResult.reason),
          t("published.ted.cpvCodesError", "Kunde inte hämta CPV-koder"),
        );
      }

      if (buyerResult.status === "fulfilled") {
        setBuyerOptions(buyerResult.value.map((name) => ({ label: name, value: name })));
      } else {
        NotificationManager.error(
          String(buyerResult.reason),
          t("published.ted.buyersError", "Kunde inte hämta beställare"),
        );
      }

      setLoadingFilters(false);
    })();
  }, [t]);

  const cpvCodes = useMemo(
    () => selectedCpv.map((option) => option?.value).filter(Boolean) as string[],
    [selectedCpv],
  );

  const buyers = useMemo(
    () => selectedBuyers.map((option) => option?.value).filter(Boolean) as string[],
    [selectedBuyers],
  );

  /**
   * What a save and the list query both stand on, derived once so they cannot disagree.
   *
   * A category is stored as a category, so the bevakning follows later corrections to the CPV
   * mapping instead of freezing today's expansion. Pruning a code out of a category's expansion
   * drops the category in the CPV handler, where the user's intent is known - not here, where
   * an empty control cannot be told apart from a deliberately emptied one.
   *
   * Until the CPV vocabulary has loaded there is nothing to say about codes, so the categories
   * stand alone and no code is sent either way.
   */
  const cpvVocabularyReady = cpvOptions.length > 0;
  const watchFilter = useMemo(() => {
    if (!cpvVocabularyReady) return { areaIds, cpvCodes: undefined as string[] | undefined };
    const fromCategories = new Set(cpvCodesForCategories(areaIds, categories));
    return { areaIds, cpvCodes: cpvCodes.filter((code) => !fromCategories.has(code)) };
  }, [areaIds, categories, cpvCodes, cpvVocabularyReady]);

  const categoryOptions = useMemo(
    () => categories.map((category) => ({ label: category.name, value: category.id })),
    [categories],
  );

  const selectedCategories = useMemo(
    () => categoryOptions.filter((option) => areaIds.includes(String(option.value))),
    [categoryOptions, areaIds],
  );

  // Grouped by division: the list runs to every code the sync has seen, which is far too
  // many to scan flat.
  const cpvGroups = useMemo(() => {
    const byDivision = new Map<string, { label: string, options: SelectorType[] }>();
    for (const entry of cpvOptions) {
      const group = byDivision.get(entry.division)
        || { label: `${entry.division} ${entry.divisionLabel}`, options: [] };
      group.options.push({ label: `${entry.code} – ${entry.label}`, value: entry.code });
      byDivision.set(entry.division, group);
    }
    // Array.from, not a spread: the es5 target cannot iterate a Map's values.
    return Array.from(byDivision.values());
  }, [cpvOptions]);

  /**
   * Picking a yrkeskategori fills its CPV codes into the CPV control, so the mapping is
   * visible and adjustable rather than applied invisibly on the server. Only the codes go
   * to the API; the category is the shortcut that selected them.
   */
  const onCategoryChange = useCallback((value: MultiValue<SelectorType>) => {
    const chosen = value.map((option) => String(option?.value ?? "")).filter(Boolean);
    // A category with no CPV code in the TED data is not offered here at all, so one chosen on
    // the private tab is absent from `value` and replacing the shared list would delete it.
    const offered = new Set(categoryOptions.map((option) => String(option.value)));
    setAreaIds([...areaIds.filter((id) => !offered.has(id)), ...chosen]);
  }, [areaIds, categoryOptions, setAreaIds]);

  /**
   * Removing a code the user never picked by hand means the category that brought it in no
   * longer describes what they want. Dropping the category here, rather than inferring it
   * later from the state of the control, keeps the shared filter the single account of it -
   * otherwise the other tab still shows the category and one click there brings the code back.
   */
  const previousAreaIds = useRef<string[]>([]);
  const onCpvChange = useCallback((value: MultiValue<SelectorType>) => {
    const next = value.map((option) => String(option?.value ?? "")).filter(Boolean);
    const kept = categoriesStillCovered(areaIds, categories, next);
    if (kept.length !== areaIds.length) {
      // The seeding effect withdraws the codes a departing category brought in. A category
      // dropped here departs because one of its codes was removed and the rest are still
      // wanted, so the transition is recorded as already reconciled.
      previousAreaIds.current = kept;
      setAreaIds(kept);
    }
    setSelectedCpv(value);
  }, [areaIds, categories, setAreaIds]);

  /**
   * The CPV control follows the chosen categories, so the mapping is visible and adjustable
   * rather than applied invisibly on the server. Categories are shared with the other tab, so
   * this runs for one picked there too - which is why it takes the codes the departing
   * categories brought in and adds what the arriving ones bring, instead of replacing the
   * selection wholesale and discarding whatever the user picked by hand.
   */
  const seededCpv = useRef(false);
  useEffect(() => {
    if (!filter.ready || !cpvOptions.length) return;
    const byCode = new Map(cpvOptions.map((entry) => [entry.code, entry]));

    const leaving = new Set(cpvCodesForCategories(previousAreaIds.current, categories));
    const arriving = cpvCodesForCategories(areaIds, categories);
    previousAreaIds.current = areaIds;

    setSelectedCpv((current) => {
      const wanted = new Set(current
        .map((option) => String(option?.value ?? ""))
        .filter((code) => code && !leaving.has(code)));
      arriving.forEach((code) => wanted.add(code));
      if (!seededCpv.current) {
        seededCpv.current = true;
        filter.initialCpvCodes.forEach((code) => wanted.add(code));
      }
      return Array.from(wanted)
        .filter((code) => byCode.has(code))
        .map((code) => ({
          label: `${code} – ${byCode.get(code)!.label}`,
          value: code,
        }));
    });
  }, [areaIds, categories, cpvOptions, filter.ready, filter.initialCpvCodes]);

  const fetchPage = useCallback((query: Query<TedNotice>): Promise<QueryResult<TedNotice>> => {
    const order = query.orderByCollection?.[0];
    const sortKey = order?.orderByField ? SORT_KEY_BY_FIELD[order.orderByField] : undefined;
    const sort = sortKey
      ? `${order.orderDirection === "desc" ? "-" : ""}${sortKey}`
      : undefined;

    return serviceSales
      .fetchTedNotices({
        page: query.page,
        pageSize: query.pageSize,
        search: debouncedSearch || undefined,
        areaIds: watchFilter.areaIds.length ? watchFilter.areaIds : undefined,
        cpvCodes: watchFilter.cpvCodes?.length ? watchFilter.cpvCodes : undefined,
        buyers: buyers.length ? buyers : undefined,
        places: places.length ? places : undefined,
        sort,
      })
      .then((result) => {
        setTotalCount(result.totalCount);
        return { data: result.data, page: result.page, totalCount: result.totalCount };
      })
      .catch((e) => {
        NotificationManager.error(
          (e as Error).toString(),
          t("published.ted.error", "Kunde inte hämta upphandlingar från TED"),
        );
        // An empty page keeps the app's own empty state instead of material-table's
        // untranslated error row.
        setTotalCount(0);
        return { data: [], page: 0, totalCount: 0 };
      });
  }, [debouncedSearch, watchFilter, buyers, places, t]);

  // In remote mode material-table ignores a changed `data` function, so paging and sorting
  // refetch by themselves but a filter change needs poking.
  //
  // Compared by content, not identity: choosing a category commits twice, the vocabularies
  // arriving give `watchFilter` a new identity describing the same question, and switching
  // tabs is navigation rather than a filter change. What the table already shows is recorded
  // only when a query is actually issued, so an edit made on the other tab while this one is
  // hidden stays owed until it returns, and is then spent once.
  const onScreen = useRef<string | null>(null);
  useEffect(() => {
    // Keyed on what the query carries: cpvCodes goes from undefined to [] when the vocabulary
    // lands, and both reach the API as no codes at all.
    const asked = JSON.stringify([
      debouncedSearch, watchFilter.areaIds, watchFilter.cpvCodes ?? [], buyers, places,
    ]);
    if (onScreen.current === null) {
      onScreen.current = asked;
      return;
    }
    if (hidden || asked === onScreen.current) return;
    onScreen.current = asked;
    tableRef.current?.onQueryChange({ page: 0 });
  }, [debouncedSearch, watchFilter, buyers, places, hidden]);

  // Explicit widths with a fixed layout: auto layout re-measures from content, so sorting
  // one column shifts the others.
  const columns = useMemo(() => [
    {
      title: t("table.header.tedTitle", "Upphandling"),
      field: "title",
      width: "31%",
      render: (row: TedNotice) => (
        <div>
          <div className={styles.title} title={row.title}>{row.title}</div>
          <div className={styles.publicationNumber}>{row.publicationNumber}</div>
        </div>
      ),
    },
    {
      title: t("table.header.org", "Beställare"),
      field: "buyer.name",
      width: "13%",
      sorting: false,
    },
    {
      title: t("table.header.category", "Yrkeskategori"),
      field: "category.name",
      width: "14%",
      sorting: false,
      render: (row: TedNotice) => {
        const cell = categoryCell(row.category, row.cpv);
        if (cell.kind !== "cpv") return cell.text;
        return (
          <span
            className={styles.cpvFallback}
            title={t(
              "published.ted.cpvFallbackHint",
              "TED:s egen kategori (CPV). Inte kopplad till en yrkeskategori i Accurator.",
            )}
          >
            {cell.text}
          </span>
        );
      },
    },
    {
      title: t("table.header.region", "Region"),
      field: "places",
      width: "12%",
      sorting: false,
      render: (row: TedNotice) => {
        const { text, title } = placeSummary(row.places);
        return <span title={title} style={{ whiteSpace: "nowrap" }}>{text}</span>;
      },
    },
    {
      title: t("table.header.publishedAt", "Publicerad"),
      field: "publicationDate",
      width: "10%",
      render: (row: TedNotice) => formatDate(row.publicationDate),
    },
    {
      title: t("table.header.timeLeft", "Anbudstid"),
      field: "deadline",
      width: "9%",
      render: (row: TedNotice) => {
        if (!row.deadline) return "–";
        const daysLeft = getDaysLeft(row.deadline);
        return (
          <span style={{ whiteSpace: "nowrap" }}>
            {isNaN(+daysLeft)
              ? daysLeft
              : t("table.row.timeLeft", "{{count}} dagar", {
                context: +daysLeft < 0 ? "expired" : null,
                count: +daysLeft,
              })}
          </span>
        );
      },
    },
    {
      title: t("table.header.tedValue", "Värde"),
      field: "estimatedValue",
      width: "11%",
      render: (row: TedNotice) => formatValue(row.estimatedValue, row.estimatedValueCurrency),
    },
  ], [t]);

  const renderDetailPanel = useCallback(
    ({ rowData }: { rowData: TedNotice }) => <TedNoticeDetailPanel notice={rowData} />,
    [],
  );

  if (loadingFilters) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
        <Loading type="inline" />
      </div>
    );
  }

  return (
    <div>
      <InfoBox
        customStyle={{ marginBottom: "24px" }}
        description={t(
          "published.ted.intro",
          "Offentliga upphandlingar i Sverige, publicerade i EU:s annonsdatabas TED. Anbud lämnas hos beställaren, inte i Accurator.",
        )}
      />

      <SearchBarWithActions className={styles.filters} value={search} onChange={setSearch}>
        <div className={styles.filterRow}>
          <Selector
            isClearable
            isMulti
            isSearchable
            placeholder={t("published.ted.categorySelector", "Yrkeskategori")}
            options={categoryOptions}
            value={selectedCategories}
            onChange={onCategoryChange}
            isOverflow
          />
          <Selector
            isClearable
            isMulti
            isSearchable
            placeholder={t("published.ted.cpvSelector", "CPV-kod")}
            options={cpvGroups}
            value={selectedCpv}
            onChange={onCpvChange}
            virtualized
            isOverflow
          />
          <Selector
            isClearable
            isMulti
            isSearchable
            placeholder={t("published.ted.buyerSelector", "Beställare")}
            options={buyerOptions}
            value={selectedBuyers}
            onChange={(value: MultiValue<SelectorType>) => setSelectedBuyers(value)}
            virtualized
            isOverflow
          />
          <PlaceSelector
            places={places}
            onChange={setPlaces}
            noTooltip
            placeholderInsteadOfLabel
            isOverflow
          />
          {filter.enabled && (
            <div className={styles.watchAction}>
              <AppTenderWatchButton
                areaIds={watchFilter.areaIds}
                places={places}
                cpvCodes={watchFilter.cpvCodes}
                saved={filter.saved}
                onSaved={filter.markSaved}
              />
            </div>
          )}
        </div>
      </SearchBarWithActions>

      <div className={styles.table}>
          <DataTable<TedNotice>
          tableRef={tableRef}
          columns={columns}
          data={fetchPage}
          isLoading={false}
          pageSize={PAGE_SIZE}
          tableLayout="fixed"
          // material-table types the third argument as the toggle or a RowAction, and only
          // one of those is callable.
          onRowClick={(_event, _rowData, togglePanel) => {
            if (typeof togglePanel === "function") togglePanel();
          }}
          // DataTable's empty-state guard only inspects an array `data`, so a remote table
          // must withhold the panel itself or the empty row grows a phantom column.
          detailPanel={totalCount > 0 ? renderDetailPanel : undefined}
          defaultOrderBy={columns.findIndex((column) => column.field === DEFAULT_SORT_FIELD)}
          defaultOrderByField={DEFAULT_SORT_FIELD}
          defaultOrderDirection="desc"
        />
      </div>

      <p className={styles.attribution}>
        {t(
          "published.ted.attribution",
          "Källa: TED (Tenders Electronic Daily), Europeiska unionens publikationsbyrå. Vidareutnyttjande enligt beslut 2011/833/EU.",
        )}{" "}
        <a href="https://ted.europa.eu" target="_blank" rel="noopener noreferrer">
          {t("published.ted.attributionLink", "Om TED")}
        </a>
      </p>
    </div>
  );
};

export default TedTable;
